import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, MapPin, Briefcase, RefreshCw, Building2, ExternalLink, Globe, Star } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobBoard() {
  const [jobs, setJobs] = useState([]);
  const [ioaCareers, setIoaCareers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('AI ML Data Science');
  const [location, setLocation] = useState('New York, Pennsylvania, New Jersey');
  const [totalResults, setTotalResults] = useState(0);
  const [activeTab, setActiveTab] = useState('marketplace');
  // If the user submitted a resume via /career-advisor, we have their personalized match %
  const [resumeSubmitted, setResumeSubmitted] = useState(false);

  useEffect(() => {
    searchRealJobsOnMount();
    fetchIoaCareers();
    try {
      const stored = sessionStorage.getItem('ioa_resume_match');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Only treat as valid within 24h
        if (parsed.ts && (Date.now() - parsed.ts) < 24 * 60 * 60 * 1000) {
          setResumeSubmitted(true);
        }
      }
    } catch {}
  }, []);

  const fetchIoaCareers = async () => {
    try {
      const { data } = await axios.get(`${API}/careers`);
      setIoaCareers(data.jobs || []);
    } catch {}
  };

  const searchRealJobsOnMount = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/jobs/search`, { query: 'AI ML Data Science', location: 'New York', count: 15 }, { withCredentials: true });
      if (data.jobs?.length) {
        setJobs(data.jobs);
        setTotalResults(data.total || data.jobs.length);
      }
    } catch {
      // Fall back to cached
      try {
        const { data } = await axios.get(`${API}/jobs`, { withCredentials: true });
        setJobs(data.jobs || []);
      } catch {}
    }
    setLoading(false);
  };

  const fetchCachedJobs = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/jobs`, { withCredentials: true });
      setJobs(data.jobs || []);
    } catch {}
    setLoading(false);
  };

  const searchRealJobs = async () => {
    setSearching(true);
    try {
      const { data } = await axios.post(`${API}/jobs/search`, { query, location, count: 15 }, { withCredentials: true });
      if (data.jobs?.length) {
        setJobs(data.jobs);
        setTotalResults(data.total || data.jobs.length);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Job search failed. Try again.');
    }
    setSearching(false);
  };

  return (
    <div data-testid="job-board" className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title="AI/ML Jobs Board — Real-Time Listings"
        description="Live AI, ML, and Data Science jobs from Indeed, LinkedIn, Glassdoor and 1000+ boards. Default region: PA, NY, NJ tri-state. Plus internal Institute of Analytics careers."
        path="/jobs"
      />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Career Opportunities</p>
          <h1 data-testid="jobs-heading" className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Real-Time Job Board
          </h1>
          <p className="text-base text-white/80 max-w-2xl">
            Live job listings powered by Adzuna — aggregated from Indeed, LinkedIn, Glassdoor, and thousands of job boards. Updated in real-time.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-0 border-b border-[#262626] mb-8">
          <button onClick={() => setActiveTab('marketplace')} className={`px-6 py-3 text-sm font-medium border-b-2 flex items-center gap-2 transition-colors ${activeTab === 'marketplace' ? 'border-[#CC0000] text-[#F59E0B]' : 'border-transparent text-zinc-400 hover:text-zinc-200'}`}>
            <Globe className="w-4 h-4" strokeWidth={1.5} /> Marketplace Jobs
          </button>
          <button onClick={() => setActiveTab('institute')} className={`px-6 py-3 text-sm font-medium border-b-2 flex items-center gap-2 transition-colors ${activeTab === 'institute' ? 'border-[#CC0000] text-[#F59E0B]' : 'border-transparent text-zinc-400 hover:text-zinc-200'}`}>
            <Building2 className="w-4 h-4" strokeWidth={1.5} /> Institute of Analytics Jobs
          </button>
        </div>

        {activeTab === 'institute' ? (
          /* IoA Careers Tab */
          <div>
            <div className="mb-6 p-4 bg-[#111111] border border-[#262626]">
              <p className="text-base text-white font-semibold mb-1">CRM Portals LLC — Parent Company of Institute of Analytics USA (TM)</p>
              <p className="text-sm text-zinc-400">Direct employment opportunities for our graduates and experienced professionals.</p>
            </div>
            {ioaCareers.length === 0 ? (
              <div className="text-center py-16 border border-[#262626] bg-[#111111]">
                <Briefcase className="w-16 h-16 text-zinc-400 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-zinc-300">No institute positions currently open.</p>
              </div>
            ) : (
              <div className="border border-[#262626]">
                {ioaCareers.map((job, i) => (
                  <div key={job.id || i} data-testid={`ioa-career-${i}`} className="p-6 border-b border-[#262626] last:border-b-0 bg-[#111111] hover:bg-[#1a1a1a] transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{job.title}</h3>
                        <p className="text-sm text-[#0077B6]">{job.company}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-mono text-[#F59E0B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{job.salary_range || ''}</span>
                        <p className="text-xs text-zinc-400 mt-1">{job.location} &middot; {job.job_type}</p>
                      </div>
                    </div>
                    <p className="text-sm text-zinc-300 mb-3">{job.description}</p>
                    {job.requirements && (
                      <div className="flex flex-wrap gap-1.5">
                        {job.requirements.map((r, j) => (
                          <span key={j} className="text-xs font-mono px-2 py-1 bg-[#0077B6]/10 text-[#0077B6] border border-[#0077B6]/20" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{r}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Marketplace Jobs Tab */
          <>
        {/* Search */}
        <div className="border border-[#262626] bg-[#111111] p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Search Query</label>
              <div className="flex items-center border border-[#262626] bg-[#0A0A0A] px-3">
                <Search className="w-5 h-5 text-zinc-400" strokeWidth={1.5} />
                <input
                  data-testid="job-search-query"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchRealJobs()}
                  className="flex-1 px-3 py-3 text-base bg-transparent text-white placeholder-zinc-500 outline-none"
                  placeholder="e.g., Machine Learning Engineer, Data Scientist"
                />
              </div>
            </div>
            <div className="w-full md:w-64">
              <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Location</label>
              <div className="flex items-center border border-[#262626] bg-[#0A0A0A] px-3">
                <MapPin className="w-5 h-5 text-zinc-400" strokeWidth={1.5} />
                <input
                  data-testid="job-search-location"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchRealJobs()}
                  className="flex-1 px-3 py-3 text-base bg-transparent text-white placeholder-zinc-500 outline-none"
                  placeholder="City, State, or Country"
                />
              </div>
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={searchRealJobs}
                disabled={searching}
                data-testid="search-real-jobs-btn"
                className="brand-btn text-base px-6 py-3 flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
              >
                {searching ? (
                  <><RefreshCw className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Searching...</>
                ) : (
                  <><Globe className="w-4 h-4" strokeWidth={1.5} /> Search Live Jobs</>
                )}
              </button>
            </div>
          </div>
          {totalResults > 0 && (
            <p className="text-sm text-zinc-400 mt-3">{totalResults.toLocaleString()} real jobs found for "{query}"</p>
          )}
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="text-center py-16 text-zinc-400">Loading jobs...</div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-16 border border-[#262626] bg-[#111111]">
            <Briefcase className="w-16 h-16 text-zinc-400 mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-zinc-300 text-lg mb-2">No jobs yet</p>
            <p className="text-base text-zinc-400 mb-6">Click "Search Live Jobs" to find real AI/ML/DS positions from the job market</p>
          </div>
        ) : (
          <>
            <div className="border border-[#262626]">
              <div className="grid grid-cols-12 gap-4 p-4 bg-[#111111] border-b border-[#262626] text-xs font-mono uppercase tracking-[0.2em] text-zinc-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                <div className="col-span-4">Position</div>
                <div className="col-span-2">Company</div>
                <div className="col-span-2">Location</div>
                <div className="col-span-2">Salary</div>
                <div className="col-span-2">{resumeSubmitted ? 'Match %' : 'Fit*'}</div>
              </div>
              {jobs.map((job, i) => {
                return (
                  <div
                    key={job.id || i}
                    data-testid={`job-row-${i}`}
                    className="grid grid-cols-12 gap-4 p-4 border-b border-[#262626] last:border-b-0 hover:bg-[#111111] transition-colors items-center text-base"
                  >
                    <div className="col-span-4">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-white">{job.title}</p>
                        {job.apply_url && (
                          <a href={job.apply_url} target="_blank" rel="noopener noreferrer" className="text-[#0077B6] hover:text-white" data-testid={`apply-link-${i}`}>
                            <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                          </a>
                        )}
                      </div>
                      <p className="text-sm text-zinc-400 mt-0.5 line-clamp-1">{job.description}</p>
                      {(() => {
                        const d = job.posted_days_ago;
                        if (d === null || d === undefined) return null;
                        const label = d === 0 ? 'today' : d === 1 ? 'yesterday' : d < 7 ? `${d} days ago` : d < 30 ? `${Math.floor(d/7)} week${Math.floor(d/7) > 1 ? 's' : ''} ago` : d < 365 ? `${Math.floor(d/30)} month${Math.floor(d/30) > 1 ? 's' : ''} ago` : 'over a year ago';
                        const fresh = d <= 7;
                        return (
                          <p className={`text-xs mt-1 font-mono ${fresh ? 'text-green-500' : 'text-zinc-500'}`} data-testid={`job-posted-${i}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {fresh ? '● ' : ''}Posted {label}
                          </p>
                        );
                      })()}
                    </div>
                    <div className="col-span-2 flex items-center gap-1.5">
                      <Building2 className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                      <span className="text-zinc-300">{job.company}</span>
                    </div>
                    <div className="col-span-2 flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                      <span className="text-zinc-300 truncate">{job.location}</span>
                    </div>
                    <div className="col-span-2 font-mono text-sm text-zinc-300" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {job.salary_range || '—'}
                    </div>
                    <div className="col-span-2" data-testid={`job-fit-${job.id || ''}`}>
                      {resumeSubmitted ? (
                        <span className={`text-xs font-mono px-2 py-1 ${
                          job.match_score >= 90 ? 'bg-green-900/30 text-green-400' :
                          job.match_score >= 80 ? 'bg-blue-900/30 text-blue-400' :
                          'bg-zinc-800 text-zinc-400'
                        }`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {job.match_score}%
                        </span>
                      ) : (
                        <div
                          className="flex flex-col gap-0.5 cursor-help"
                          title={`IoA curriculum fit: ${job.curriculum_stars || 3}/5${job.curriculum_matched_areas?.length ? `\nAreas matched: ${job.curriculum_matched_areas.join(', ')}` : ''}`}
                        >
                          <div className="flex items-center gap-0.5">
                            {[1,2,3,4,5].map(n => (
                              <Star
                                key={n}
                                className="w-3.5 h-3.5"
                                strokeWidth={1.5}
                                fill={n <= (job.curriculum_stars || 3) ? '#F59E0B' : 'none'}
                                color={n <= (job.curriculum_stars || 3) ? '#F59E0B' : '#52525b'}
                              />
                            ))}
                          </div>
                          {job.curriculum_matched_areas?.length > 0 && (
                            <p className="text-[10px] text-zinc-500 font-mono leading-tight line-clamp-1" data-testid={`job-fit-breakdown-${i}`}>
                              {job.curriculum_matched_areas.slice(0, 2).join(', ')}{job.curriculum_matched_areas.length > 2 ? ` +${job.curriculum_matched_areas.length - 2}` : ''}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Fit / Match explainer */}
            <div data-testid="match-score-footnote" className="mt-4 p-4 bg-[#111111] border border-[#262626]">
              {resumeSubmitted ? (
                <p className="text-sm text-zinc-300 italic">
                  <span className="font-semibold text-white not-italic">* Personalized Match:</span> Scores reflect your uploaded resume mapped against each role's requirements. Keywords, experience, and target-role input all factor in.
                </p>
              ) : (
                <p className="text-sm text-zinc-300 italic">
                  <span className="font-semibold text-[#F59E0B] not-italic">* Curriculum Fit (1-5 stars):</span> How well the IoA USA PG Diploma prepares a graduate for this role — based on skill overlap across all 6 courses.
                  {' '}
                  <Link to="/career-advisor" className="text-[#0077B6] hover:underline">Upload your resume</Link> to get a personalized % match instead.
                </p>
              )}
            </div>
          </>
        )}
          </>
        )}
      </div>
    </div>
  );
}
